import React, { useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import "../styles/ItemForm.css"; // Reuse some styles for structure
import { useGlobal } from "../context/GlobalContext";
import { searchProductos } from "../services/productos";
import { Producto } from "../types";

const ConteoCiego: React.FC = () => {
    const { reLoad } = useGlobal();
    const navigate = useNavigate();

    const [busqueda, setBusqueda] = useState("");
    const [searchResults, setSearchResults] = useState<Producto[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const [selectedProduct, setSelectedProduct] = useState<Producto | null>(null);
    const [cantidadContada, setCantidadContada] = useState<number | "">("");
    const [motivo, setMotivo] = useState("Ajuste por conteo físico");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const buscarProducto = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSelectedProduct(null);
        setHasSearched(false);
        setSearchResults([]);

        if (!busqueda.trim()) return;

        try {
            setLoading(true);
            const res = await searchProductos(busqueda, 1, 20);
            if (res.data && res.data.length > 0) {
                setSearchResults(res.data);
                // Si es un coincidencia exacta (y única) por código de barras, podríamos auto-seleccionar
                const firstProd = res.data[0];
                if (res.data.length === 1 && firstProd && (firstProd.codigo_barras === busqueda || firstProd.referencia === busqueda)) {
                    seleccionarProducto(firstProd);
                }
            } else {
                setError("No se encontraron productos con ese término.");
            }
        } catch (err: any) {
            setError(err.message || "Error al buscar productos.");
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    };

    const seleccionarProducto = (prod: Producto) => {
        setSelectedProduct(prod);
        setSearchResults([]);
        setCantidadContada("");
        setError(null);
        setSuccess(null);
    };

    const registrarConteo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedProduct || cantidadContada === "") return;

        try {
            setLoading(true);
            await api.post(
                "/inventario/ajuste",
                {
                    producto_id: selectedProduct.id,
                    cantidad_contada: Number(cantidadContada),
                    motivo,
                }
            );
            setSuccess(`Conteo ciego registrado para ${selectedProduct.nombre}`);
            setSelectedProduct(null);
            setBusqueda("");
            setCantidadContada("");
            setHasSearched(false);
            reLoad();
        } catch (err: any) {
            setError(err.response?.data?.error || "Error al registrar el conteo.");
        } finally {
            setLoading(false);
        }
    };

    const cancelarSeleccion = () => {
        setSelectedProduct(null);
        setCantidadContada("");
        setMotivo("Ajuste por conteo físico");
    };

    return (
        <div className="container">
            <h1 className="page-title">Conteo Ciego (Ajuste Físico)</h1>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {!selectedProduct && (
                <div className="form-container">
                    <form onSubmit={buscarProducto} className="search-form">
                        <div className="form-group">
                            <label>Buscar Producto</label>
                            <input
                                type="text"
                                className="form-input"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                placeholder="Nombre, código o referencia..."
                                autoFocus
                            />
                        </div>
                        <button type="submit" className="button submit" disabled={loading}>
                            {loading ? "Buscando..." : "Buscar"}
                        </button>
                    </form>
                </div>
            )}

            {!selectedProduct && hasSearched && searchResults.length > 0 && (
                <div className="search-results" style={{ marginTop: '20px' }}>
                    <h3 style={{ marginBottom: '15px' }}>Resultados ({searchResults.length}):</h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {searchResults.map((prod) => (
                            <li
                                key={prod.id}
                                onClick={() => seleccionarProducto(prod)}
                                style={{
                                    padding: '12px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: '#fff',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                            >
                                <div>
                                    <strong style={{ fontSize: '1.1em', display: 'block', color: '#1e293b' }}>{prod.nombre}</strong>
                                    <span style={{ color: '#64748b', fontSize: '0.9em' }}>
                                        {[prod.marca, prod.modelo, prod.referencia].filter(Boolean).join(' - ')}
                                    </span>
                                </div>
                                <div style={{ color: '#10b981', fontWeight: 'bold' }}>
                                    Seleccionar ➔
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {selectedProduct && (
                <div className="form-container" style={{ marginTop: '20px' }}>
                    <div className="product-info-blind" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                        <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>{selectedProduct.nombre}</h3>
                        <p style={{ margin: '4px 0', color: '#475569' }}><strong>Marca:</strong> {selectedProduct.marca || "N/A"}</p>
                        <p style={{ margin: '4px 0', color: '#475569' }}><strong>Referencia:</strong> {selectedProduct.referencia || "N/A"}</p>
                        <p style={{ margin: '15px 0 0 0', color: '#eab308', fontWeight: 'bold', fontSize: '0.95em' }}>
                            ⚠️ No se muestra el stock del sistema. Ingrese únicamente la cantidad física contada.
                        </p>
                    </div>

                    <form onSubmit={registrarConteo} className="count-form">
                        <div className="form-group">
                            <label>Cantidad Contada (Física)</label>
                            <input
                                type="number"
                                required
                                min="0"
                                className="form-input count-input"
                                value={cantidadContada}
                                onChange={(e) => setCantidadContada(e.target.value ? Number(e.target.value) : "")}
                                placeholder="Ejem: 15"
                                style={{ fontSize: '1.2em', padding: '12px' }}
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label>Motivo</label>
                            <input
                                type="text"
                                className="form-input"
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                placeholder="Razón del ajuste"
                            />
                        </div>

                        <div className="button-group" style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
                            <button type="button" className="button cancel" onClick={cancelarSeleccion} style={{ flex: 1 }}>
                                Volver a Buscar
                            </button>
                            <button type="submit" className="button submit" disabled={loading || cantidadContada === ""} style={{ flex: 2 }}>
                                {loading ? "Registrando..." : "Registrar Ajuste Físico"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ConteoCiego;
